import { CopyIcon, DownloadIcon, EyeIcon, RefreshIcon } from "@xola/icons";
import { flash } from "@xola/ui-kit";
import { SecondaryButton } from "common";
import React, { useCallback } from "react";
import { useInvalidate } from "@/hooks/useInvalidation";

export interface PhotoActionButtonsProps {
    blobUrl: string;
    blob?: Blob;
    altText: string;
    photoPath?: string;
    buttonClassName?: string;
    onViewFullSize?: (e: React.MouseEvent) => void;
}

const convertToPng = async (blob: Blob): Promise<Blob> =>
    new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(blob);
        img.addEventListener("load", () => {
            URL.revokeObjectURL(url);
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext("2d")!.drawImage(img, 0, 0);
            canvas.toBlob((png) => (png ? resolve(png) : reject(new Error("Canvas toBlob failed"))), "image/png");
        });
        img.addEventListener("error", () => {
            URL.revokeObjectURL(url);
            reject(new Error("Image load failed"));
        });
        img.src = url;
    });

export const PhotoActionButtons = ({
    blobUrl,
    blob,
    altText,
    photoPath,
    buttonClassName = "!p-2",
    onViewFullSize,
}: PhotoActionButtonsProps) => {
    const invalidate = useInvalidate();

    const handleDownload = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            try {
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = `${altText.replace(/\s+/g, "_")}.jpg`;
                document.body.append(link);
                link.click();
                link.remove();
            } catch {
                flash.danger("Failed to download image");
            }
        },
        [blobUrl, altText],
    );

    const handleCopy = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation();
            try {
                if (!blob) {
                    flash.danger("Image data not available for copying");
                    return;
                }
                const pngBlob = await convertToPng(blob);
                await navigator.clipboard.write([new ClipboardItem({ "image/png": pngBlob })]);
                flash.success("Image copied to clipboard");
            } catch {
                flash.danger("Failed to copy image to clipboard");
            }
        },
        [blob],
    );

    const handleRefresh = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            invalidate("questionnaire-photo", photoPath);
            flash.success("Image refreshed");
        },
        [invalidate, photoPath],
    );

    return (
        <>
            {onViewFullSize && (
                <SecondaryButton
                    size="small"
                    title="View full size"
                    className={buttonClassName}
                    onClick={onViewFullSize}
                >
                    <EyeIcon />
                </SecondaryButton>
            )}
            <SecondaryButton size="small" title="Download image" className={buttonClassName} onClick={handleDownload}>
                <DownloadIcon />
            </SecondaryButton>
            <SecondaryButton size="small" title="Copy to clipboard" className={buttonClassName} onClick={handleCopy}>
                <CopyIcon />
            </SecondaryButton>
            <SecondaryButton size="small" title="Refresh image" className={buttonClassName} onClick={handleRefresh}>
                <RefreshIcon />
            </SecondaryButton>
        </>
    );
};
